/**
 * Where the backend lives.
 *
 * Historically the frontend and the backend were always served from the same
 * origin (nginx served the built site and reverse-proxied the API), so every
 * call could use a root-relative path like `/api/games/token/` and every socket
 * could be built from `window.location`.
 *
 * For the public deployment the static site is hosted separately from the game
 * backend, so the origin has to be injected at build time:
 *
 *     VITE_BACKEND_ORIGIN=https://api.transcendence.sonam.me npm run build
 *
 * Leave the variable unset and everything resolves to the current origin,
 * which is exactly the previous behaviour. That keeps the local dev stack
 * working with no configuration at all.
 *
 * Note the two are same-site (both under sonam.me), so cookies still flow with
 * `credentials: 'include'` and SameSite=Lax; no third-party cookie involved.
 */

// Written exactly like this on purpose: Vite statically replaces the literal
// text `import.meta.env.VITE_BACKEND_ORIGIN` at build time. Optional chaining
// (`import.meta.env?.VITE_...`) does not match that pattern and would silently
// ship an empty origin.
const configured = String(import.meta.env.VITE_BACKEND_ORIGIN || '').trim().replace(/\/+$/, '')

/** Origin prefix for HTTP calls. Empty string means "same origin". */
export const BACKEND_ORIGIN = configured

/**
 * Build an absolute URL for a backend path.
 * @param {string} path root-relative path, e.g. '/api/games/token/'
 */
export function backendUrl(path) {
    return `${BACKEND_ORIGIN}${path}`
}

/**
 * Origin to hand to socket.io. It needs a real origin, never an empty string,
 * so fall back to the page's own origin when nothing is configured.
 */
export function socketOrigin() {
    if (BACKEND_ORIGIN) {
        return BACKEND_ORIGIN
    }
    return `${window.location.protocol}//${window.location.host}`
}
